import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Shield, X, ExternalLink } from "lucide-react";

/**
 * Checkbox obbligatoria "Ho letto e accetto la Privacy Policy"
 * Il link "Privacy Policy" apre la policy dell'agenzia in un overlay senza cambiare pagina.
 *
 * Props:
 *  - agencyId: string        ID dell'agenzia titolare (owner del form)
 *  - checked: boolean        stato del checkbox
 *  - onChange: fn(bool)      callback
 *  - required: boolean       (default true)
 *  - testid: string          suffix per data-testid
 */
export default function PrivacyConsentCheckbox({ agencyId, checked, onChange, required = true, testid = "privacy" }) {
    const [showPolicy, setShowPolicy] = useState(false);
    return (
        <>
            <label
                data-testid={`${testid}-consent-label`}
                className={`flex items-start gap-3 text-xs cursor-pointer select-none ${checked ? "text-ink-100" : "text-ink-300"}`}
            >
                <input
                    data-testid={`${testid}-consent-checkbox`}
                    type="checkbox"
                    required={required}
                    checked={!!checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-gold shrink-0"
                />
                <span>
                    Ho letto e accetto la{" "}
                    <button
                        type="button"
                        data-testid={`${testid}-consent-link`}
                        onClick={(e) => { e.preventDefault(); setShowPolicy(true); }}
                        className="text-gold underline underline-offset-2 hover:text-gold/80 inline-flex items-center gap-1"
                    >
                        Privacy Policy <ExternalLink size={11} />
                    </button>
                    {" "}dell&apos;agenzia (obbligatorio)
                </span>
            </label>
            {showPolicy && <PrivacyPolicyOverlay agencyId={agencyId} onClose={() => setShowPolicy(false)} />}
        </>
    );
}

export function PrivacyPolicyOverlay({ agencyId, onClose }) {
    const [policy, setPolicy] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        if (!agencyId) { setError("Agenzia non specificata"); return; }
        api.get(`/privacy/policy/agency/${agencyId}`)
            .then((r) => setPolicy(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Impossibile caricare la policy"));
    }, [agencyId]);

    // Chiudi con Escape
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            data-testid="privacy-policy-overlay"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-3xl max-h-[85vh] border border-gold/30 bg-ink-950 flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div className="flex items-center gap-2.5">
                        <Shield size={16} className="text-gold" />
                        <div>
                            <div className="font-serif text-lg text-ink-50">Privacy Policy</div>
                            {policy && (
                                <div className="text-[10px] text-ink-400 uppercase tracking-wider">
                                    {policy.agency_name} · versione {policy.version}
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="privacy-overlay-close"
                        className="text-ink-400 hover:text-ink-50 border border-white/10 p-1.5"
                        aria-label="Chiudi"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 text-sm text-ink-100">
                    {error && <div className="text-red-300">{error}</div>}
                    {!error && !policy && <div className="text-ink-400">Caricamento...</div>}
                    {policy && policy.has_pdf && (
                        <div className="h-[65vh]">
                            <iframe
                                title="Privacy Policy PDF"
                                src={`${process.env.REACT_APP_BACKEND_URL}${policy.pdf_url}`}
                                className="w-full h-full border border-white/10 bg-white"
                            />
                        </div>
                    )}
                    {policy && !policy.has_pdf && policy.content_text && (
                        <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-ink-100">
                            {policy.content_text}
                        </pre>
                    )}
                </div>
                <div className="border-t border-white/10 px-6 py-3 flex justify-end">
                    <button onClick={onClose} className="cta-outline px-4 py-2 text-xs" data-testid="privacy-overlay-ok">
                        Ho letto
                    </button>
                </div>
            </div>
        </div>
    );
}
